import {observer} from 'mobx-react-lite';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {FiChevronDown} from 'react-icons/fi';
import {useDebouncedCallback} from 'use-debounce';
import {useActionItems} from '@hooks/features-action-items';
import {useFirebaseFetch} from '@hooks/query-hook';
import {useStoreContext} from '@store/store-context';
import {MyGoalCard} from '../my-objective/my-goals-v2';
import {
  emptyMyGoalsFilters,
  MyGoalsFilters,
} from '../my-objective/my-goals-v2.types';
import {
  filterMyGoals,
  goalMatchesSearch,
  summarizeGoalEngagement,
  summarizeMyGoals,
  GoalEngagementSummary,
} from '../my-objective/my-goals-v2.utils';
import {useDirectReportHook} from '../direct-reports/direct-report-hook';
import {TeamGoalsToolbar} from '../direct-reports/team-goals-toolbar';
import {teamMemberName} from '../direct-reports/team-goals.utils';
import {EmptyPanel, LoadingPanel} from '../direct-reports/styles';
import {ItemLoader} from '@ui/organisms/item-loader';
import {
  allGoalGroup,
  allGoalOwner,
  allGoalTeam,
  buildAllGoalSections,
  flattenAllGoals,
  goalEntityId,
} from '../all-goals/all-goals.utils';
import {AlignmentOverview} from './alignment-overview';
import {
  AlignmentBranchButton,
  AlignmentCardSlot,
  AlignmentContent,
  AlignmentEmpty,
  AlignmentExpandButton,
  AlignmentLeafMarker,
  AlignmentTreeChildren,
  AlignmentTreeItem as AlignmentTreeItemStyle,
  AlignmentTreeList,
  AlignmentTreeRow,
  AlignmentTreeSection,
  UnalignedHeader,
} from './alignment.styles';
import {AlignmentNode, AlignmentSpotlightState} from './alignment.types';
import {
  alignmentAncestorClosure,
  alignmentEntityId,
  alignmentSpotlight,
  buildAlignmentForest,
  expandableAlignmentIds,
  pruneAlignmentForest,
} from './alignment.utils';

type AlignmentScope = 'my' | 'team' | 'all';

const noOp = () => undefined;
const emptyEngagement: GoalEngagementSummary = {
  commentCount: 0,
  unreadCommentCount: 0,
  actionItemCount: 0,
  openActionItemCount: 0,
  overdueActionItemCount: 0,
  dueTodayActionItemCount: 0,
  dueSoonActionItemCount: 0,
  unreadActionItemCount: 0,
  allActionItemsCompleted: false,
};

const AlignmentTreeGoal = ({
  node,
  nested = false,
  lastSibling = false,
  expandedIds,
  onToggle,
  spotlightState,
  spotlightEnabled,
  users,
  currentUserId,
  engagement,
}: {
  node: AlignmentNode;
  nested?: boolean;
  lastSibling?: boolean;
  expandedIds: Set<string>;
  onToggle: (id: string) => void;
  spotlightState: (id: string) => AlignmentSpotlightState;
  spotlightEnabled: boolean;
  users: any[];
  currentUserId: string;
  engagement: Record<string, GoalEngagementSummary>;
}) => {
  const expanded = expandedIds.has(node.id);
  const hasChildren = node.children.length > 0;
  const state = spotlightEnabled ? spotlightState(node.id) : 'related';
  const owner = allGoalOwner(node.goal, users);
  const ownerId = goalEntityId(owner);
  const reviewerId = goalEntityId(owner?.reviewer);
  const canManage = ownerId === currentUserId || reviewerId === currentUserId;
  const managerEdit = ownerId !== currentUserId && reviewerId === currentUserId;

  return (
    <AlignmentTreeItemStyle
      $nested={nested}
      $lastSibling={lastSibling}
      role="treeitem"
      aria-expanded={hasChildren ? expanded : undefined}>
      <AlignmentTreeRow
        $nested={nested}
        $lastSibling={lastSibling}
        $hasExpandedChildren={hasChildren && expanded}>
        {hasChildren ? (
          <AlignmentBranchButton
            type="button"
            $expanded={expanded}
            aria-label={`${
              expanded ? 'Collapse' : 'Expand'
            } aligned goals for ${node.goal.name}`}
            onClick={() => onToggle(node.id)}>
            <FiChevronDown aria-hidden="true" />
          </AlignmentBranchButton>
        ) : (
          <AlignmentLeafMarker aria-hidden="true" />
        )}
        <AlignmentCardSlot $dimmed={state === 'dimmed'}>
          <MyGoalCard
            goal={node.goal}
            availableUsers={users}
            engagement={engagement[node.id] || emptyEngagement}
            expanded={false}
            onToggle={noOp}
            compact
            highlighted={state === 'owned'}
            showResultsDisclosure={false}
            additionalMetadata={
              hasChildren
                ? `${node.children.length} aligned goal${
                    node.children.length === 1 ? '' : 's'
                  }`
                : undefined
            }
            canManage={canManage}
            managerEdit={managerEdit}
          />
        </AlignmentCardSlot>
      </AlignmentTreeRow>

      {hasChildren && expanded && (
        <AlignmentTreeChildren role="group">
          {node.children.map((child, index) => (
            <AlignmentTreeGoal
              key={child.id}
              node={child}
              nested
              lastSibling={index === node.children.length - 1}
              expandedIds={expandedIds}
              onToggle={onToggle}
              spotlightState={spotlightState}
              spotlightEnabled={spotlightEnabled}
              users={users}
              currentUserId={currentUserId}
              engagement={engagement}
            />
          ))}
        </AlignmentTreeChildren>
      )}
    </AlignmentTreeItemStyle>
  );
};

export const AlignmentGoalsPage = observer(
  ({scope}: {scope: AlignmentScope}) => {
    const {
      loading,
      error,
      goalMembers,
      dateRange,
      setDateRange,
      refetch,
    } = useDirectReportHook('all');
    const {
      authStore: {auth},
      usersStore: {users},
      groupStore: {groups},
      groupTypeStore: {groupType},
    } = useStoreContext();
    const firebaseGoals = useFirebaseFetch('goals');
    const goalMentions = useFirebaseFetch(
      `users/${auth.user.id}/mentions/goals`,
    );
    const goalActionItems = useActionItems({
      source: 'goal',
      itemId: '',
      userId: '',
      fetchAll: true,
    });

    const [filters, setFilters] = useState<MyGoalsFilters>(emptyMyGoalsFilters);
    const [selectedPeople, setSelectedPeople] = useState<string[]>([]);
    const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
    const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
    const [defaultPeriod] = useState(dateRange);
    const previousTreeSignature = useRef('');
    const debouncedSearch = useDebouncedCallback(
      (value: string) => setSearch(value),
      200,
    );

    useEffect(() => {
      setSelectedPeople([]);
      setSelectedGroups([]);
      setSelectedTeams([]);
    }, [scope]);

    const directReports = useMemo(
      () =>
        users.filter(
          (user: any) => alignmentEntityId(user?.reviewer) === auth.user.id,
        ),
      [auth.user.id, users],
    );
    const objectives = useMemo(
      () => flattenAllGoals(goalMembers).filter((goal) => !goal?.isKpi),
      [goalMembers],
    );
    const periodGoals = useMemo(
      () =>
        filterMyGoals({
          goals: objectives,
          period: dateRange,
          filters: emptyMyGoalsFilters,
          lifecycle: 'all',
        }),
      [dateRange, objectives],
    );
    const memberOptions = useMemo(() => {
      const ownerIds = new Set(
        periodGoals.map((goal) => alignmentEntityId(goal?.assignee)),
      );
      const available =
        scope === 'team' ? directReports : scope === 'all' ? users : [];
      return available
        .map((user: any) => ({
          id: alignmentEntityId(user),
          name: teamMemberName(user),
        }))
        .filter((option) => option.id && ownerIds.has(option.id))
        .sort((first, second) => first.name.localeCompare(second.name));
    }, [directReports, periodGoals, scope, users]);
    const groupTypeLabel = useMemo(() => {
      const namedTypes = groupType.filter((type: any) => type?.name);
      const customType = namedTypes.find(
        (type: any) =>
          type.isDefault !== true && String(type.isDefault) !== 'true',
      );
      return customType?.name || namedTypes[0]?.name || 'Groups';
    }, [groupType]);
    const emptyGroupLabel = `No ${groupTypeLabel
      .replace(/s$/i, '')
      .toLowerCase()}`;
    const groupOptions = useMemo(
      () =>
        buildAllGoalSections(
          periodGoals,
          users,
          groups,
          'group',
          emptyGroupLabel,
        )
          .filter((section) => section.kind === 'group')
          .map((section) => ({id: section.id, name: section.name})),
      [emptyGroupLabel, groups, periodGoals, users],
    );
    const teamOptions = useMemo(
      () =>
        buildAllGoalSections(periodGoals, users, groups, 'team')
          .filter((section) => section.kind === 'team')
          .map((section) => ({id: section.id, name: section.name})),
      [groups, periodGoals, users],
    );
    const forest = useMemo(() => buildAlignmentForest(periodGoals), [
      periodGoals,
    ]);
    const summary = useMemo(() => summarizeMyGoals(periodGoals), [periodGoals]);

    const matchingGoals = useMemo(
      () =>
        filterMyGoals({
          goals: periodGoals,
          period: dateRange,
          filters,
          lifecycle: 'all',
        }).filter((goal) => {
          if (!goalMatchesSearch(goal, search)) return false;
          if (
            selectedPeople.length &&
            !selectedPeople.includes(alignmentEntityId(goal?.assignee))
          ) {
            return false;
          }
          if (
            selectedGroups.length &&
            !selectedGroups.includes(
              allGoalGroup(goal, users, groups, emptyGroupLabel).id,
            )
          ) {
            return false;
          }
          if (
            selectedTeams.length &&
            !selectedTeams.includes(allGoalTeam(goal, users).id)
          ) {
            return false;
          }
          return true;
        }),
      [
        dateRange,
        emptyGroupLabel,
        filters,
        groups,
        periodGoals,
        search,
        selectedGroups,
        selectedPeople,
        selectedTeams,
        users,
      ],
    );
    const visibleIds = useMemo(
      () =>
        alignmentAncestorClosure(
          forest,
          matchingGoals.map((goal) => alignmentEntityId(goal)),
        ),
      [forest, matchingGoals],
    );
    const visibleForest = useMemo(
      () => pruneAlignmentForest(forest, visibleIds),
      [forest, visibleIds],
    );
    const expandableIds = useMemo(() => expandableAlignmentIds(visibleForest), [
      visibleForest,
    ]);
    const visibleTreeSignature = Array.from(visibleIds).sort().join('|');

    useEffect(() => {
      if (previousTreeSignature.current === visibleTreeSignature) return;
      setExpandedIds((current) => {
        const retained = new Set(
          Array.from(current).filter((id) => expandableIds.has(id)),
        );
        if (!previousTreeSignature.current) {
          visibleForest.alignedRoots.forEach((root) => {
            if (root.children.length) retained.add(root.id);
          });
        }
        if (search.trim()) {
          expandableIds.forEach((id) => retained.add(id));
        }
        return retained;
      });
      previousTreeSignature.current = visibleTreeSignature;
    }, [
      expandableIds,
      search,
      visibleForest.alignedRoots,
      visibleTreeSignature,
    ]);

    const spotlightOwnerIds = useMemo(() => {
      if (scope === 'my') return [auth.user.id];
      if (scope === 'team') {
        return directReports.map((user: any) => alignmentEntityId(user));
      }
      return [];
    }, [auth.user.id, directReports, scope]);
    const spotlight = useMemo(
      () => alignmentSpotlight(forest, spotlightOwnerIds),
      [forest, spotlightOwnerIds],
    );

    const engagementByGoal = useMemo(
      () =>
        periodGoals.reduce((result, goal) => {
          result[goal.id] = summarizeGoalEngagement({
            goal,
            firebaseGoal: firebaseGoals.data?.[goal.id],
            mentions: goalMentions.data?.[goal.id],
            actionItems: goalActionItems.tasks,
            commentsLoading: firebaseGoals.isLoading,
            actionItemsLoading: goalActionItems.isLoading,
          });
          return result;
        }, {} as Record<string, GoalEngagementSummary>),
      [
        firebaseGoals.data,
        firebaseGoals.isLoading,
        goalActionItems.isLoading,
        goalActionItems.tasks,
        goalMentions.data,
        periodGoals,
      ],
    );

    const toggleNode = useCallback((id: string) => {
      setExpandedIds((current) => {
        const next = new Set(current);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
      });
    }, []);
    const allExpanded =
      expandableIds.size > 0 &&
      Array.from(expandableIds).every((id) => expandedIds.has(id));
    const toggleAll = useCallback(() => {
      setExpandedIds(allExpanded ? new Set() : new Set(expandableIds));
    }, [allExpanded, expandableIds]);

    const pageLabel =
      scope === 'my'
        ? 'My Goals'
        : scope === 'team'
        ? 'Team Goals'
        : 'All Goals';

    if (loading) {
      return (
        <LoadingPanel aria-label="Loading goal alignment">
          <ItemLoader />
        </LoadingPanel>
      );
    }

    if (error) {
      return (
        <EmptyPanel role="alert">
          <div>
            <h2>Goal alignment could not be loaded</h2>
            <p>Check your connection and try again.</p>
            <button type="button" onClick={() => refetch()}>
              Try again
            </button>
          </div>
        </EmptyPanel>
      );
    }

    if (objectives.length === 0) {
      return (
        <AlignmentEmpty>
          <div>
            <h2>No objectives to align yet</h2>
            <p>Objectives will appear here once they have been created.</p>
          </div>
        </AlignmentEmpty>
      );
    }

    return (
      <AlignmentContent>
        <AlignmentOverview
          pageLabel={pageLabel}
          averageProgress={summary.averageProgress}
          alignedCount={forest.alignedIds.size}
          unalignedCount={forest.unalignedIds.size}
        />

        <TeamGoalsToolbar
          pageLabel={`${pageLabel} alignment`}
          goals={periodGoals}
          filters={filters}
          sort="highest_progress"
          groupBy="alignment"
          period={dateRange}
          defaultPeriod={defaultPeriod}
          memberOptions={memberOptions}
          selectedPeople={selectedPeople}
          peopleLabel={scope === 'team' ? 'Direct reports' : 'Owners'}
          selectionFilterGroups={
            scope === 'all'
              ? [
                  ...(groupOptions.length
                    ? [
                        {
                          key: 'groups',
                          title: groupTypeLabel,
                          options: groupOptions,
                          selected: selectedGroups,
                          onChange: setSelectedGroups,
                        },
                      ]
                    : []),
                  ...(teamOptions.length
                    ? [
                        {
                          key: 'teams',
                          title: 'Teams',
                          options: teamOptions,
                          selected: selectedTeams,
                          onChange: setSelectedTeams,
                        },
                      ]
                    : []),
                ]
              : []
          }
          searchValue={searchInput}
          searchPlaceholder="Search goals"
          filterSearchable
          includeKpiFilter={false}
          showSort={false}
          showGroupBy={false}
          toolbarAction={
            <AlignmentExpandButton
              type="button"
              disabled={expandableIds.size === 0}
              onClick={toggleAll}>
              {allExpanded ? 'Collapse all' : 'Expand all'}
            </AlignmentExpandButton>
          }
          resultCount={matchingGoals.length}
          onSearchChange={(value) => {
            setSearchInput(value);
            debouncedSearch(value);
          }}
          onFiltersChange={setFilters}
          onSortChange={noOp}
          onGroupByChange={noOp}
          onPeriodChange={setDateRange}
          onSelectedPeopleChange={setSelectedPeople}
        />

        {matchingGoals.length === 0 ? (
          <AlignmentEmpty>
            <div>
              <h2>No goals match these filters</h2>
              <p>Try clearing a filter or using another search.</p>
            </div>
          </AlignmentEmpty>
        ) : (
          <AlignmentTreeSection>
            {visibleForest.alignedRoots.length > 0 && (
              <AlignmentTreeList role="tree" aria-label="Aligned goals">
                {visibleForest.alignedRoots.map((root) => (
                  <AlignmentTreeGoal
                    key={root.id}
                    node={root}
                    expandedIds={expandedIds}
                    onToggle={toggleNode}
                    spotlightState={spotlight.stateFor}
                    spotlightEnabled={
                      scope !== 'all' && spotlight.ownedIds.size > 0
                    }
                    users={users}
                    currentUserId={auth.user.id}
                    engagement={engagementByGoal}
                  />
                ))}
              </AlignmentTreeList>
            )}

            {visibleForest.unalignedRoots.length > 0 && (
              <>
                <UnalignedHeader>
                  <h2>Unaligned</h2>
                  <p>Goals not laddering up to a company objective</p>
                </UnalignedHeader>
                <AlignmentTreeList role="tree" aria-label="Unaligned goals">
                  {visibleForest.unalignedRoots.map((root) => (
                    <AlignmentTreeGoal
                      key={root.id}
                      node={root}
                      expandedIds={expandedIds}
                      onToggle={toggleNode}
                      spotlightState={spotlight.stateFor}
                      spotlightEnabled={false}
                      users={users}
                      currentUserId={auth.user.id}
                      engagement={engagementByGoal}
                    />
                  ))}
                </AlignmentTreeList>
              </>
            )}
          </AlignmentTreeSection>
        )}
      </AlignmentContent>
    );
  },
);
